<template>
  <div class="video-player-container">
    <div v-if="error" class="error-message">
      <font-awesome-icon icon="exclamation-triangle" />
      {{ error }}
      <button v-if="canRetry" @click="retryPlayback" class="retry-button">
        <font-awesome-icon icon="sync" /> Retry
      </button>
    </div>
    <div v-else-if="!streamUrl" class="loading-message">
      <font-awesome-icon icon="spinner" spin />
      Loading video...
    </div>
    <div v-else class="player-wrapper">
      <video ref="videoElement" class="video-player" controls></video>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { FontAwesomeIcon } from '@/scripts/icons';
import Hls from 'hls.js';

const props = defineProps({
  streamUrl: {
    type: String,
    default: ''
  }
});

// Track current playback time
const currentTime = ref(0);

// Update current time when video time changes
const updateCurrentTime = () => {
  if (videoElement.value) {
    currentTime.value = videoElement.value.currentTime;
  }
};

defineExpose({
  seek: (timestamp) => {
    if (videoElement.value) {
      videoElement.value.currentTime = timestamp;
    }
  },
  getCurrentTime: () => currentTime.value
});

const videoElement = ref(null);
const hls = ref(null);
const error = ref('');
const canRetry = ref(false);
const recoveryAttempts = ref(0);
const maxRecoveryAttempts = 3;
const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const initializePlayer = () => {
  if (!props.streamUrl) return;
  
  // Reset state
  error.value = '';
  canRetry.value = false;
  recoveryAttempts.value = 0;
  
  // Clean up previous HLS instance if it exists
  if (hls.value) {
    hls.value.destroy();
    hls.value = null;
  }
  
  // Make sure the video element is clean
  if (videoElement.value) {
    videoElement.value.pause();
    videoElement.value.src = '';
    videoElement.value.load();
  }
  
  if (Hls.isSupported()) {
    try {
      // Default config settings
      const hlsConfig = {
        debug: false,
        fragLoadingMaxRetry: 6,
        manifestLoadingMaxRetry: 6,
        levelLoadingMaxRetry: 6,
        fragLoadingRetryDelay: 500,
        manifestLoadingRetryDelay: 500,
        levelLoadingRetryDelay: 500,
        autoStartLoad: true
      };
      
      // Firefox-specific settings
      if (isFirefox) {
        // Additional Firefox-specific settings
        Object.assign(hlsConfig, {
           // Start with a lower bandwidth estimate
          abrEwmaDefaultEstimate: 500000,
           // Disable adaptive bitrate testing
          testBandwidth: false,
          // Disable ABR (Adaptive Bitrate) for Firefox to prevent codec switching
          // ABR often selects higher quality levels with codecs Firefox doesn't support well
          abrEnabled: false,
          // Lock at level 0 (lowest quality)
          startLevel: 0,
          capLevelToPlayerSize: false,
          // Cap the max buffer size to avoid memory pressure
          maxBufferLength: 15,
          maxMaxBufferLength: 30,
          // Give more priority to stable playback than quality
          backBufferLength: 30
        });
      }
      
      // Create the HLS instance with our config
      hls.value = new Hls(hlsConfig);
      
      // Bind HLS to the video element
      hls.value.attachMedia(videoElement.value);
      
      // Load the source
      hls.value.loadSource(props.streamUrl);
      
      // Handle media attached
      hls.value.on(Hls.Events.MEDIA_ATTACHED, () => {});
      
      // Handle level loaded - force Firefox to use level 0
      hls.value.on(Hls.Events.LEVEL_LOADED, (event, data) => {
        // If Firefox, force level 0 to prevent switching to incompatible codecs
        if (isFirefox && hls.value && data.level > 0) {
          hls.value.currentLevel = 0;
          hls.value.loadLevel = 0;
        }
      });
      
      // Handle level switched - keep Firefox at level 0
      hls.value.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        // Maintain level 0 for Firefox
        if (isFirefox && data.level > 0 && hls.value) {
          hls.value.currentLevel = 0;
        }
      });
      
      // Handle manifest parsed
      hls.value.on(Hls.Events.MANIFEST_PARSED, () => {
        // If on Firefox, explicitly set the level to 0 (lowest quality)
        // which typically uses the most compatible codec
        if (isFirefox && hls.value) {
          hls.value.currentLevel = 0;
          hls.value.loadLevel = 0;
        }
      });
      
      // Handle HLS errors with recovery
      hls.value.on(Hls.Events.ERROR, (event, data) => {
        console.warn('HLS error encountered', data.type, data.details, data);
        
        if (data.fatal) {
          switch(data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (recoveryAttempts.value < maxRecoveryAttempts) {
                recoveryAttempts.value++;
                setTimeout(() => {
                  hls.value.startLoad();
                }, 500); // Add a delay before retrying
              } else {
                error.value = 'Network error: Unable to load the video after multiple attempts';
                canRetry.value = true;
              }
              break;
              
            case Hls.ErrorTypes.MEDIA_ERROR:
              if (recoveryAttempts.value < maxRecoveryAttempts) {
                recoveryAttempts.value++;
                
                // Try different recovery strategies based on attempt number
                if (recoveryAttempts.value === 1) {
                  hls.value.recoverMediaError();
                } else {
                  // On subsequent attempts, try more aggressive recovery
                  hls.value.swapAudioCodec();
                  hls.value.recoverMediaError();
                }
              } else {
                error.value = 'Media decode error: This video format may not be compatible with your browser';
                canRetry.value = true;
              }
              break;
              
            default:
              error.value = 'Cannot play this video: streaming error';
              canRetry.value = true;
              hls.value.destroy();
              hls.value = null;
              break;
          }
        }
      });
      
      // Add video element error listener for unhandled errors
      addVideoElementListener('error', () => {
        if (!error.value) {
          error.value = `Browser error: ${videoElement.value.error?.message || 'Unknown error'}`;
          canRetry.value = true;
        }
      });
      
      // Set up timeupdate listener to track current playback time
      setupTimeUpdateListener();
      
    } catch (e) {
      console.error('Error initializing HLS.js:', e);
      error.value = `Failed to initialize video player: ${e.message}`;
      canRetry.value = true;
    }
  } else if (videoElement.value.canPlayType('application/vnd.apple.mpegurl')) {
    // For Safari which has built-in HLS support
    try {
      videoElement.value.src = props.streamUrl;
      
      // Just add error listener
      addVideoElementListener('error', () => {
        error.value = `Browser error: ${videoElement.value.error?.message || 'Unknown error'}`;
        canRetry.value = true;
      });
    } catch (e) {
      error.value = `Failed to play video: ${e.message}`;
      canRetry.value = true;
    }
  } else {
    error.value = 'HLS playback is not supported in this browser.';
    canRetry.value = false;
  }
};

// Method to retry playback after error
const retryPlayback = () => {
  error.value = '';
  canRetry.value = false;
  recoveryAttempts.value = 0;
  
  // Small delay before retrying
  setTimeout(() => {
    initializePlayer();
  }, 500);
};

// Watch for changes in the stream URL
watch(() => props.streamUrl, (newUrl) => {
  if (newUrl) {
    initializePlayer();
  }
});

onMounted(() => {
  // Small delay to ensure DOM is fully rendered
  setTimeout(() => {
    if (props.streamUrl) {
      initializePlayer();
    }
  }, 100);
});

// Store references to event listeners so we can properly clean them up
const videoElementListeners = ref([]);

// Helper to safely add event listeners that will be cleaned up on unmount
const safeAddEventListener = (event, handler) => {
  if (!videoElement.value) return;
  
  videoElement.value.addEventListener(event, handler);
  videoElementListeners.value.push({ event, handler });
};

// We'll use this wrapped version for all video element event listeners
const addVideoElementListener = (event, callback) => {
  const handler = (e) => {
    if (videoElement.value) {
      callback(e);
    }
  };
  
  safeAddEventListener(event, handler);
};

// Add timeupdate event listener when the player is initialized
const setupTimeUpdateListener = () => {
  addVideoElementListener('timeupdate', () => {
    updateCurrentTime();
  });
};

onBeforeUnmount(() => {
  // First, remove all event listeners to prevent callback errors
  if (videoElementListeners.value.length > 0 && videoElement.value) {
    videoElementListeners.value.forEach(({ event, handler }) => {
      try {
        videoElement.value.removeEventListener(event, handler);
      } catch (e) {
        console.warn('Failed to remove event listener:', e);
      }
    });
    videoElementListeners.value = [];
  }
  
  // Clean up HLS
  if (hls.value) {
    try {
      hls.value.destroy();
    } catch (e) {
      console.warn('Error during HLS cleanup:', e);
    }
    hls.value = null;
  }
  
  // Clean up video element
  if (videoElement.value) {
    try {
      videoElement.value.pause();
      videoElement.value.removeAttribute('src');
      videoElement.value.load();
    } catch (e) {
      console.warn('Error cleaning up video element:', e);
    }
  }
});
</script>

<style scoped>
@import '@/styles/video-player.css';
</style>
